import { Box, Card, Heading, Text } from "@chakra-ui/react"
import type { CorrectnessDataPoint } from "../../../client"

interface CorrectnessOverTimeChartProps {
  data: CorrectnessDataPoint[]
}

export default function CorrectnessOverTimeChart({
  data,
}: CorrectnessOverTimeChartProps) {
  if (data.length === 0) {
    return (
      <Card.Root h="full">
        <Card.Body>
          <Heading size="md" mb={4}>
            Correctness Over Time
          </Heading>
          <Text color="gray.500">
            No data available for the selected period
          </Text>
        </Card.Body>
      </Card.Root>
    )
  }

  // Calculate min/max for scaling
  const percentages = data.map((d) => d.correct_percentage)
  const maxPercentage = Math.max(...percentages, 100)
  const minPercentage = Math.min(...percentages, 0)

  return (
    <Card.Root h="full">
      <Card.Body>
        <Heading size="md" mb={4}>
          Correctness Over Time
        </Heading>

        <Box position="relative" h="250px" mt={4}>
          {/* Y-axis labels */}
          <Box position="absolute" left={0} top={0} bottom={0} w="40px">
            <Text fontSize="xs" position="absolute" top="0" right="8px">
              {maxPercentage.toFixed(0)}%
            </Text>
            <Text
              fontSize="xs"
              position="absolute"
              top="50%"
              right="8px"
              transform="translateY(-50%)"
            >
              {((maxPercentage + minPercentage) / 2).toFixed(0)}%
            </Text>
            <Text fontSize="xs" position="absolute" bottom="0" right="8px">
              {minPercentage.toFixed(0)}%
            </Text>
          </Box>

          {/* Chart area */}
          <Box
            position="absolute"
            left="45px"
            right="0"
            top="0"
            bottom="30px"
            borderLeft="1px"
            borderBottom="1px"
            borderColor="gray.300"
          >
            {/* Data points and line */}
            <svg width="100%" height="100%" style={{ overflow: "visible" }}>
              <title>Correctness percentage over time line chart</title>
              {data.map((point, idx) => {
                const x = (idx / (data.length - 1 || 1)) * 100
                const y =
                  100 -
                  ((point.correct_percentage - minPercentage) /
                    (maxPercentage - minPercentage || 1)) *
                    100

                const nextPoint = data[idx + 1]
                if (nextPoint) {
                  const nextX = ((idx + 1) / (data.length - 1 || 1)) * 100
                  const nextY =
                    100 -
                    ((nextPoint.correct_percentage - minPercentage) /
                      (maxPercentage - minPercentage || 1)) *
                      100

                  return (
                    <g key={idx}>
                      <line
                        x1={`${x}%`}
                        y1={`${y}%`}
                        x2={`${nextX}%`}
                        y2={`${nextY}%`}
                        stroke="#3182CE"
                        strokeWidth="2"
                      />
                      <circle cx={`${x}%`} cy={`${y}%`} r="4" fill="#3182CE" />
                    </g>
                  )
                }

                return (
                  <circle
                    key={idx}
                    cx={`${x}%`}
                    cy={`${y}%`}
                    r="4"
                    fill="#3182CE"
                  />
                )
              })}
            </svg>
          </Box>

          {/* X-axis labels */}
          <Box position="absolute" left="45px" right="0" bottom="0" h="25px">
            <Text fontSize="xs" position="absolute" left="0">
              {new Date(data[0].date).toLocaleDateString()}
            </Text>
            {data.length > 1 && (
              <Text fontSize="xs" position="absolute" right="0">
                {new Date(data[data.length - 1].date).toLocaleDateString()}
              </Text>
            )}
          </Box>
        </Box>
      </Card.Body>
    </Card.Root>
  )
}
